"use client";

import type { LucideIcon } from "lucide-react";
import { Button, type ButtonProps } from "@/components/forms/Button";
import { Typography } from "@/components/common/ui/Typography";

export interface BulkAction {
  /** Button label */
  label: string;
  /** Lucide icon to display */
  icon?: LucideIcon;
  /** Click handler */
  onClick: () => void;
  /** Button variant — defaults to "primary" */
  variant?: ButtonProps["variant"];
  /** Disable the action */
  disabled?: boolean;
  /** Show loading spinner */
  loading?: boolean;
}

export interface BulkActionsProps {
  /** Number of currently selected items */
  selectedCount: number;
  /** Singular noun for the item type, e.g. "submission", "user" */
  itemLabel: string;
  /** Action buttons to render */
  actions: BulkAction[];
  /** Called when "Clear selection" is clicked */
  onClearSelection: () => void;
}

export function BulkActions({
  selectedCount,
  itemLabel,
  actions,
  onClearSelection,
}: BulkActionsProps) {
  if (selectedCount <= 0) return null;

  const pluralLabel = selectedCount === 1 ? itemLabel : `${itemLabel}s`;

  return (
    <div
      role="toolbar"
      aria-label={`Bulk actions for ${selectedCount} selected ${pluralLabel}`}
      className="flex items-center justify-between px-4 py-3 bg-blue-50 border border-blue-200 rounded-lg"
    >
      <Typography as="span" variant="body" weight="medium" color="text-blue-800">
        {selectedCount} {pluralLabel} selected
      </Typography>
      <div className="flex items-center gap-2">
        {actions.map((action) => (
          <Button
            key={action.label}
            variant={action.variant ?? "primary"}
            size="md"
            onClick={action.onClick}
            icon={action.icon}
            disabled={action.disabled}
            loading={action.loading}
          >
            {action.label}
          </Button>
        ))}
        <Button variant="ghost" size="md" onClick={onClearSelection}>
          Clear selection
        </Button>
      </div>
    </div>
  );
}

BulkActions.displayName = "BulkActions";
